import { useState } from "react";
import cx from "classnames";

import {
  Button,
  Dropdown,
  DropdownSelectionType,
  Flex,
  Icon,
  Input,
  Radio,
  type DropdownItem,
} from "@repo/ping-design";

import { formatWithCommas } from "../../../utils/formatters";
import { DOLLAR_INPUT_OPTIONS, parseLiteralDollarInput, normalizeDollarInput } from "./dollarInput";
import type { EditableFieldConfig } from "./editableFields";
import { COPE_DROPDOWN_WIDTH, SEARCHABLE_OPTION_COUNT } from "./editors/DropdownCellEditor";

export type TopBuildingsListColumnBulkEdit = {
  rowKey: string;
  config: EditableFieldConfig;
  rowCount: number;
  disabled?: boolean;
  onApply: (rowKey: string, value: string | number | null) => void;
};

export type TopBuildingsListColumnHeaderProps = {
  name: string;
  title?: string;
  bulkEdit?: TopBuildingsListColumnBulkEdit;
  onHide?: () => void;
};

const BULK_EDIT_ACTION = "bulkEdit";
const HIDE_ACTION = "hide";

export function TopBuildingsListColumnHeader({ name, title, bulkEdit, onHide }: TopBuildingsListColumnHeaderProps) {
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const options: DropdownItem[] = [
    ...(bulkEdit
      ? [
          {
            value: BULK_EDIT_ACTION,
            label: `Set all ${formatWithCommas(bulkEdit.rowCount)} buildings…`,
            disabled: bulkEdit.disabled,
            behavior: "KEEP_OPEN" as const,
          },
        ]
      : []),
    ...(onHide ? [{ value: HIDE_ACTION, label: "Hide column" }] : []),
  ];
  const isCodeList = (bulkEdit?.config.options?.length ?? 0) > SEARCHABLE_OPTION_COUNT;

  return (
    <div className="TopBuildingsList__ColumnHeader">
      <span className="TopBuildingsList__ColumnHeader__Name" title={title}>
        {name}
      </span>
      {options.length > 0 && (
        <div className="TopBuildingsList__ColumnHeader__Menu" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            options={options}
            selected={null}
            customFormOpen={bulkEditOpen}
            onCustomFormOpenChange={setBulkEditOpen}
            orientation="down"
            dropdownWidth={bulkEditOpen ? (isCodeList ? COPE_DROPDOWN_WIDTH : "16rem") : "max-content"}
            dropdownMaxHeight="30rem"
            menuItemClasses="TopBuildingsList__ColumnHeader__MenuItem"
            customTrigger={
              <button
                type="button"
                className="TopBuildingsList__ColumnHeader__MenuButton"
                aria-label="Column options"
                title="Column options"
              >
                <Icon iconName="EllipsisVertical" size={14} />
              </button>
            }
            onChange={(sel) => {
              if (sel.type !== DropdownSelectionType.SimpleValue) return;
              if (sel.value === BULK_EDIT_ACTION) setBulkEditOpen(true);
              else if (sel.value === HIDE_ACTION) onHide?.();
            }}
            renderCustomForm={({ closeDropdown }) =>
              bulkEdit && <BulkEditForm name={name} bulkEdit={bulkEdit} onClose={closeDropdown} />
            }
          />
        </div>
      )}
    </div>
  );
}

type BulkEditFormProps = {
  name: string;
  bulkEdit: TopBuildingsListColumnBulkEdit;
  onClose: () => void;
};

function BulkEditForm({ name, bulkEdit, onClose }: BulkEditFormProps) {
  const { rowKey, config, rowCount, disabled = false, onApply } = bulkEdit;
  // null until something is chosen, since "" is a real option that clears the field.
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const isMoney = config.type === "money";
  const isText = config.type === "text";
  const options = config.options ?? [];
  const isCodeList = options.length > SEARCHABLE_OPTION_COUNT;
  const showSearch = !isMoney && options.length > SEARCHABLE_OPTION_COUNT;
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter((option) => `${option.value} ${option.label}`.toLowerCase().includes(normalizedQuery))
    : options;
  // Blank text isn't applied, so Apply can't clear a whole column by accident.
  const applyValue = isMoney
    ? parseLiteralDollarInput(normalizeDollarInput(pendingValue ?? ""))
    : isText
      ? pendingValue?.trim() || null
      : pendingValue;
  const canSubmit = !disabled && applyValue !== null;

  return (
    <form
      className="TopBuildingsList__BulkEditCard"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onApply(rowKey, applyValue);
        onClose();
      }}
    >
      <div className="TopBuildingsList__BulkEditCard__Title">{name}</div>
      {isMoney || isText ? (
        <Input
          name="value"
          value={pendingValue ?? ""}
          autoFocus
          prefix={isMoney ? <span>$</span> : undefined}
          options={isMoney ? DOLLAR_INPUT_OPTIONS : undefined}
          useDebounce={false}
          onChange={(_name, v) => setPendingValue(String(v))}
        />
      ) : (
        <>
          {showSearch && (
            <Input
              name="search"
              className="TopBuildingsList__BulkEditCard__Search"
              placeholder={isCodeList ? "Search code or name" : "Filter"}
              value={query}
              autoFocus
              useDebounce={false}
              suffix={<Icon iconName="Search" size={14} />}
              onChange={(_name, v) => setQuery(String(v ?? ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
            />
          )}
          <div
            className={cx("TopBuildingsList__BulkEditCard__Options", {
              "TopBuildingsList__BulkEditCard__Options--Fixed": showSearch,
            })}
          >
            {visibleOptions.length === 0 && <div className="TopBuildingsList__BulkEditCard__Empty">No matches</div>}
            {visibleOptions.map((option) => (
              <Radio
                key={option.value}
                name="value"
                value={option.value}
                label={option.label}
                checked={pendingValue === option.value}
                onChange={() => setPendingValue(option.value)}
              />
            ))}
          </div>
        </>
      )}
      <Flex direction="row" gap="2" justify="end" className="TopBuildingsList__BulkEditCard__Footer">
        <Button variant="outline" size="sm" type="button" label="Cancel" onClick={onClose} />
        <Button
          size="sm"
          type="submit"
          disabled={!canSubmit}
          label={`Apply to ${formatWithCommas(rowCount)} buildings`}
        />
      </Flex>
    </form>
  );
}
